import { useState, useMemo } from 'react';
import { ModuleHeader } from '@/components/ui/module-header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FloatingField } from '@/components/ui/floating-field';
import { formatCurrency } from '@/lib/utils';
import * as Icons from 'lucide-react';

export default function CalculatorPage() {
  const [distance, setDistance] = useState('');
  const [rate, setRate] = useState('');
  const [fuel, setFuel] = useState('');
  const [driver, setDriver] = useState('');
  const [additional, setAdditional] = useState('');

  const calc = useMemo(() => {
    const d = parseFloat(distance) || 0;
    const r = parseFloat(rate) || 0;
    const f = parseFloat(fuel) || 0;
    const dr = parseFloat(driver) || 0;
    const a = parseFloat(additional) || 0;
    const revenue = d * r;
    const totalCost = f + dr + a;
    const profit = revenue - totalCost;
    const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
    const profitPerMile = d > 0 ? profit / d : 0;
    return { revenue, totalCost, profit, margin, profitPerMile, d };
  }, [distance, rate, fuel, driver, additional]);

  const reset = () => {
    setDistance('');
    setRate('');
    setFuel('');
    setDriver('');
    setAdditional('');
  };

  const hasInput = distance || rate || fuel || driver || additional;

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Trip Calculator"
          description="Calculate profitability for potential loads"
          icon="Calculator"
          metrics={[
            { label: 'Profit', value: formatCurrency(calc.profit) },
            { label: 'Margin', value: `${calc.margin.toFixed(1)}%` },
            { label: '$/Mile', value: formatCurrency(calc.profitPerMile) },
          ]}
          actions={hasInput && <Button variant="outline" onClick={reset}><Icons.RotateCcw className="h-4 w-4 mr-2" />Reset</Button>}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle>Trip Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <FloatingField label="Distance (miles)" type="number" value={distance} onChange={(e) => setDistance(e.target.value)} />
              <FloatingField label="Rate per mile ($)" type="number" value={rate} onChange={(e) => setRate(e.target.value)} />
              <FloatingField label="Fuel cost ($)" type="number" value={fuel} onChange={(e) => setFuel(e.target.value)} />
              <FloatingField label="Driver pay ($)" type="number" value={driver} onChange={(e) => setDriver(e.target.value)} />
              <FloatingField label="Additional costs ($)" type="number" value={additional} onChange={(e) => setAdditional(e.target.value)} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Profitability Analysis
                {hasInput && (
                  calc.profit > 0
                    ? <Icons.TrendingUp className="h-5 w-5 text-green-500" />
                    : <Icons.TrendingDown className="h-5 w-5 text-destructive" />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hasInput ? (
                <div className="space-y-4">
                  <div className={`p-6 rounded-lg text-center ${calc.profit >= 0 ? 'bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800'}`}>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Net Profit</p>
                    <p className={`text-4xl font-bold font-mono ${calc.profit >= 0 ? 'text-green-600 dark:text-green-500' : 'text-destructive'}`}>
                      {formatCurrency(calc.profit)}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{calc.margin.toFixed(1)}% margin</p>
                  </div>
                  <dl className="space-y-2 pt-2">
                    <div className="flex justify-between py-2 border-b"><dt className="text-sm text-muted-foreground">Revenue</dt><dd className="font-mono font-medium">{formatCurrency(calc.revenue)}</dd></div>
                    <div className="flex justify-between py-2 border-b"><dt className="text-sm text-muted-foreground">Total Cost</dt><dd className="font-mono font-medium text-destructive">-{formatCurrency(calc.totalCost)}</dd></div>
                    <div className="flex justify-between py-2 border-b"><dt className="text-sm text-muted-foreground">Profit per mile</dt><dd className="font-mono font-medium">{formatCurrency(calc.profitPerMile)}</dd></div>
                    <div className="flex justify-between py-2"><dt className="text-sm text-muted-foreground">Margin</dt><dd className="font-mono font-medium">{calc.margin.toFixed(2)}%</dd></div>
                  </dl>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Icons.Calculator className="h-12 w-12 text-muted-foreground mb-3" />
                  <p className="font-semibold">Enter trip details</p>
                  <p className="text-sm text-muted-foreground mt-1">Start typing to see profitability calculations</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
);
}
